import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { useEffect, useRef, useState } from "react";

interface FormErrors {
    customerName?: string;
    companyName?: string;
    emailAddress?: string;
    confirmEmailAddress?: string;
    phoneNumber?: string;
    customerNumber?: string;
    customerPo?: string;
    customerJob?: string;
    orderNotes?: string;
    sendCopyToEmail?: string;
    sendCopyToComment?: string;
    billingName?: string;
    billingAddress?: string;
    billingCity?: string;
    billingProvince?: string;
    billingPostalCode?: string;
    billingCountry?: string;
    isShippingSameAsBilling?: string;
    shippingRecipientName?: string;
    shippingAddress?: string;
    shippingCity?: string;
    shippingProvince?: string;
    shippingPostalCode?: string;
    shippingCountry?: string;
}

type RFQCartType = {
    customerName: string;
    companyName: string;
    emailAddress: string;
    confirmEmailAddress: string;
    phoneNumber: number | null;
    customerNumber: string;
    customerPo: string;
    customerJob: string;
    orderNotes: string; // EC-898 Add notes field to Guest Quote Form 
    sendCopyToEmail: string;
    sendCopyToComment: string;
    billingName: string;
    billingAddress: string;
    billingCity: string;
    billingProvince: string;
    billingPostalCode: string;
    billingCountry: string;
    isShippingSameAsBilling: boolean;
    shippingRecipientName: string;
    shippingAddress: string;
    shippingCity: string;
    shippingProvince: string;
    shippingPostalCode: string;
    shippingCountry: string;
};

const initialState: RFQCartType = {
    customerName: "",
    companyName: "",
    emailAddress: "",
    confirmEmailAddress: "",
    phoneNumber: null,
    customerNumber: "",
    customerPo: "",
    customerJob: "",
    orderNotes:"",
    sendCopyToEmail: "",
    sendCopyToComment: "",
    billingName: "",
    billingAddress: "",
    billingCity: "",
    billingProvince: "",
    billingPostalCode: "",
    billingCountry: "",
    isShippingSameAsBilling: false,
    shippingRecipientName: "",
    shippingAddress: "",
    shippingCity: "",
    shippingProvince: "",
    shippingPostalCode: "",
    shippingCountry: "",
};

const useRequestForQuotesForm = () => {
    const [formData, setFormData] = useState<RFQCartType>(initialState);
    const [errors, setErrors] = useState<FormErrors>({});

    const guestRfqFormData = "guestRfqFormData";

    const inputRefs = useRef<Partial<Record<keyof RFQCartType, HTMLInputElement | null>>>({});

    const scrollToError = () => {
        const firstErrorKey = Object.keys(errors)[0] as keyof RFQCartType;
        if (inputRefs.current[firstErrorKey]) {
            inputRefs.current[firstErrorKey]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const setFormDataInLocalStorage = () => {
        // function that stores RFQ (Request for Quotation) data into session storage
        sessionStorage.setItem(guestRfqFormData, JSON.stringify(formData));
    }

    const getFormDataInLocalStorage = () => {
        // function that retrivie RFQ (Request for Quotation) data into session storage
        return JSON.parse(sessionStorage.getItem("guestRfqFormData") || "{}");
    }

    const validateForm = (fieldsToValidate: (keyof RFQCartType)[]): boolean => {
        let valid = true;
        const newErrors: FormErrors = {};

        fieldsToValidate.forEach((field) => {
            if (!formData[field] || formData[field] === "") {
                if (field !== "sendCopyToEmail") { 
                    valid = false;
                    newErrors[field] = `This is a required field.`;
                }
            }

            // Province-specific validation
            if (
                (field === "billingProvince" || field === "shippingProvince") && 
                !formData[field]
            ) {
                valid = false;
                newErrors[field] = translate(`This is a required field.`);
            }
        });
        
        if (fieldsToValidate.includes("emailAddress") || fieldsToValidate.includes("confirmEmailAddress")) {
            if (!formData.emailAddress) {
                valid = false;
                newErrors.emailAddress = translate("This is a required field.");
            } else if (!validateEmail(formData.emailAddress)) {
                valid = false;
                newErrors.emailAddress = translate("Email is invalid");
            }

            if (!formData.confirmEmailAddress) {
                valid = false;
                newErrors.confirmEmailAddress = translate("This is a required field.");
            } else if (formData.emailAddress !== formData.confirmEmailAddress) {
                valid = false;
                newErrors.confirmEmailAddress = translate("Email addresses do not match");
            }
        }

        if (fieldsToValidate.includes("shippingPostalCode")) {
            if (formData.shippingPostalCode && formData.shippingPostalCode.length !== 6) {
                valid = false;
                newErrors.shippingPostalCode = siteMessage("Postal_Code_Character_Limit_Error") as string;
            }
        }

        if (fieldsToValidate.includes("billingPostalCode")) {
            if (formData.billingPostalCode && formData.billingPostalCode.length !== 6) {
                valid = false;
                newErrors.billingPostalCode = siteMessage("Postal_Code_Character_Limit_Error") as string;
            }
        }


        if (formData.sendCopyToEmail.trim()) {
            const emails = formData.sendCopyToEmail.split(",").map(email => email.trim());
            emails.forEach(email => {
                if (!validateEmail(email)) {
                    valid = false;
                    newErrors.sendCopyToEmail = translate("One or more email addresses are invalid");
                }
            });
        }

        setErrors(newErrors);

        if (!valid) {
            scrollToError();
        }

        return valid;
    };

    useEffect(() => {
        const firstErrorKey = Object.keys(errors)[0] as keyof RFQCartType;
        if (firstErrorKey && inputRefs.current[firstErrorKey]) {
            inputRefs.current[firstErrorKey]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [errors]);

    const formatPhoneNumber = (value: string): string => {
        // Remove all non-digit characters from the input
        const cleaned = value.replace(/\D+/g, "");

        // Format the phone number as (XXX) XXX-XXXX
        if (cleaned.length <= 3) {
            return `(${cleaned}`;
        } else if (cleaned.length <= 6) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
        } else {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        }
    };

    const validatePhoneNumberFormat = (phoneNumber: string): boolean => {
        // The regex checks if the phone number is in the format "(XXX) XXX-XXXX"
        const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
        return phoneRegex.test(phoneNumber);
    };


    const handlePhoneNumberChange = (value: string) => {
    // Format the input as the user types
    const formattedPhoneNumber = formatPhoneNumber(value);

    // Set the formatted value in state
    handleChange("phoneNumber", formattedPhoneNumber);

    // Validate the phone number format
    if (formattedPhoneNumber.length === 14 && !validatePhoneNumberFormat(formattedPhoneNumber)) {
        setErrors((prevErrors) => ({
            ...prevErrors,
            phoneNumber: translate("Invalid phone number format. Use (XXX) XXX-XXXX"),
        }));
    } else if (formattedPhoneNumber.length < 14) {
        setErrors((prevErrors) => ({
            ...prevErrors,
            phoneNumber: translate("Phone number is too short. Use (XXX) XXX-XXXX"),
        }));
    } else {
        // Clear any phone number error if valid
        setErrors((prevErrors) => ({
            ...prevErrors,
            phoneNumber: "",
        }));
    }
};



    
    const handleChange = (field: keyof RFQCartType, value: any) => {
        if (field === "phoneNumber") {
            value = formatPhoneNumber(value);
        }

        if (field === "billingProvince" || field === "shippingProvince") {
        // In case province is selected, we set the ID not the name
            value = value; // It is already the ID (province ID)
        }

        if (field === "billingCountry" || field === "shippingCountry") {
            // When a country is selected, store the country ID, but display the name
            value = value.id;
        }
        
        if (field === "sendCopyToEmail") {
        // Add a comma after every email if it's not already present
            value = value
                .replace(/\s+/g, ' ') // Replace multiple spaces with a single space
                .replace(/([^,\s]+)\s+/g, '$1, ') // Add comma after email when space is typed
                .replace(/,+/g, ',') // Prevent consecutive commas
                .replace(/^,/, ''); // Trim leading commas
        }
        setFormData((prevData) => ({
            ...prevData,
            [field]: value,
        }));
        if (errors[field]) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [field]: "",
            }));
        }
    };


    useEffect(() => {
        const storedData = sessionStorage.getItem(guestRfqFormData);
        if (storedData) {
            const parsedData: RFQCartType = JSON.parse(storedData);
            setFormData(parsedData);
        }
    }, []);


    return {
        formData,
        errors,
        handleChange,
        validateForm,
        setFormDataInLocalStorage,
        getFormDataInLocalStorage,
        handlePhoneNumberChange,
        inputRefs,
    };
};

export default useRequestForQuotesForm;
